<template>
  <component
    :is="componentName"
    :page="page"
  />
</template>

<script setup>
import { useRolesStore } from '~/stores/roles';
import { useSkillsStore } from '~/stores/skills';
import { useCountriesStore } from '~/stores/countries';
import { useDynamicPageStore } from '~/stores/dynamicPage';

const LeadGenPage = defineAsyncComponent(
  () => import('~/components/LeadGenPage/LeadGenPage.vue')
);
const RolePage = defineAsyncComponent(
  () => import('~/components/RolePage/RolePage.vue')
);

const { $cmsApi } = useNuxtApp();
const config = useRuntimeConfig();
const route = useRoute();

const rolesStore = useRolesStore();
const dynamicPageStore = useDynamicPageStore();
const countriesStore = useCountriesStore();
const skillsStore = useSkillsStore();

const { data, error } = await useAsyncData(async () => {
  const previewQuery = route.query.preview
    ? `?preview=${route.query.preview}`
    : '';
  const url = `/all-pages/${route.params.slug}${previewQuery}`;

  let roles = rolesStore.roles;
  let countries = countriesStore.safeCountries;
  let skills = skillsStore.skills;

  const page = await $cmsApi.get(url);

  if (page.page_type === 'role') {
    if (!roles.length) {
      roles = await $cmsApi.get('/role-pages');
      rolesStore.setRoles(roles);
    }

    if (!countries.length) {
      countries = await $cmsApi.get('/countries/safe');
      countriesStore.setSafeCountries(countries);
    }
  } else if (!skills.length) {
    skills = await $cmsApi.get('/skills');
    skillsStore.setSkills(skills);
  }

  return { page, countries };
});

if (error.value) {
  const errorData =
    error.value?.statusCode === 410
      ? { statusCode: 410, message: 'Page Gone' }
      : { statusCode: 404, message: 'Page not found' };
  throw createError(errorData);
}

const page = data.value?.page || {};
const countries = data.value?.countries || [];
const metaData = page?.meta_data || {};

const componentName = computed(() =>
  page.page_type === 'role' ? RolePage : LeadGenPage
);
if (page.page_type) {
  dynamicPageStore.setPageType(page.page_type);
}

useHead({
  title: `Proxify - ${metaData.title || page.title}`,
  meta: [
    {
      key: 'og:title',
      property: 'og:title',
      content: metaData.title || page.title,
    },
    {
      key: 'og:description',
      name: 'description',
      property: 'og:description',
      content:
        metaData.description ||
        page.description ||
        metaData.title ||
        page.title,
    },
    {
      key: 'og:url',
      property: 'og:url',
      content: `${config.public.hostUrl}${route.path}`,
    },
    {
      key: 'og:image',
      property: 'og:image',
      content: metaData.image || page.image,
    },
    { key: 'og:type', property: 'og:type', content: 'article' },
  ].filter((x) => x.content),
  script: [
    ...(page.page_type === 'role'
      ? [
          {
            key: 'job-posting',
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
              '@context': 'http://schema.org',
              '@type': 'JobPosting',
              name: 'Proxify',
              title: page.title,
              applicantLocationRequirements: {
                '@type': 'Country',
                name: countries,
              },
              datePosted: page.updated_at,
              description:
                "<p>Proxify's mission is to connect top developers around the world with opportunities they deserve. So, it doesn't matter where you are; we are here to help you fast-track your independent career in the right direction. 🙂</p>",
              jobLocationType: 'TELECOMMUTE',
              employmentType: 'FULL_TIME',
              hiringOrganization: {
                '@type': 'Organization',
                name: 'Proxify',
                sameAs: config.public.hostUrl,
                logo: `${config.public.hostUrl}/proxify-logo.svg`,
              },
            }),
          },
        ]
      : []),
  ],
});
</script>
